import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, Mail, Plus, Trash2, RotateCcw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LEVELS = [
  { value: "reminder", auto: true },
  { value: "formal_notice_proposal", auto: false },
  { value: "partner_handoff_proposal", auto: false },
];

const TEMPLATE_BY_LEVEL = {
  reminder: ["friendly", "firm"],
  formal_notice_proposal: ["formal_notice"],
  partner_handoff_proposal: ["handoff"],
};

export default function ReminderSettings() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("tenant.update");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [replyTo, setReplyTo] = useState("");
  const [ladder, setLadder] = useState([]);
  const [partner, setPartner] = useState({
    enabled: false, partner_name: "", partner_email: "", webhook_url: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/tenant/reminder-config");
      const cfg = r.data.config || {};
      setDefaults(r.data.defaults || null);
      setEnabled(cfg.enabled !== false);
      setReplyTo(cfg.reply_to || "");
      setLadder(Array.isArray(cfg.ladder) ? cfg.ladder : []);
      setPartner(cfg.partner_handoff || {
        enabled: false, partner_name: "", partner_email: "", webhook_url: "",
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStep = (i, patch) => {
    setLadder(prev => prev.map((s, idx) => idx === i
      ? { ...s, ...patch,
          // If level changes, pick the first valid template for that level.
          ...(patch.level && !TEMPLATE_BY_LEVEL[patch.level]?.includes(s.template)
              ? { template: TEMPLATE_BY_LEVEL[patch.level][0] } : {}),
        }
      : s));
  };
  const removeStep = (i) => setLadder(prev => prev.filter((_, idx) => idx !== i));
  const addStep = () => setLadder(prev => [
    ...prev,
    { days_after_due: 3, level: "reminder", template: "friendly" },
  ]);

  const resetDefaults = () => {
    if (!defaults) return;
    setEnabled(defaults.enabled !== false);
    setReplyTo(defaults.reply_to || "");
    setLadder(defaults.ladder || []);
    setPartner(defaults.partner_handoff || partner);
  };

  const validate = () => {
    for (const s of ladder) {
      const d = Number(s.days_after_due);
      if (!Number.isFinite(d) || d < 0) return "Délais invalides (jours ≥ 0).";
      if (!LEVELS.some(l => l.value === s.level)) return `Niveau invalide : ${s.level}`;
      if (s.level !== "reminder" && !s.template) return "Modèle manquant.";
    }
    if (partner.enabled) {
      if (!partner.partner_name?.trim()) return "Nom du partenaire requis.";
      if (!partner.partner_email?.trim() && !partner.webhook_url?.trim()) {
        return "Email ou webhook du partenaire requis.";
      }
    }
    if (replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyTo)) {
      return "Reply-To invalide.";
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.patch("/tenant/reminder-config", {
        enabled,
        reply_to: replyTo || null,
        ladder: ladder.map(s => ({
          days_after_due: Number(s.days_after_due),
          level: s.level,
          template: s.template || "",
        })),
        partner_handoff: {
          enabled: !!partner.enabled,
          partner_name: partner.partner_name || null,
          partner_email: partner.partner_email || null,
          webhook_url: partner.webhook_url || null,
        },
      });
      toast.success(t("reminderSettings.saved"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
    </div>
  );

  const sortedSteps = [...ladder].sort((a, b) =>
    Number(a.days_after_due) - Number(b.days_after_due));

  return (
    <div className="space-y-6" data-testid="reminder-settings-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          {t("sidebar.reminderSettings")}
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("reminderSettings.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("reminderSettings.subtitle")}</p>
      </div>

      {/* Enabled + Reply-To */}
      <Card className="p-6 space-y-5" data-testid="reminder-general-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-slate-900">{t("reminderSettings.enabledLabel")}</div>
            <div className="text-xs text-slate-500 mt-0.5 max-w-md">
              {t("reminderSettings.enabledHelp")}
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled}
                  disabled={!canEdit}
                  data-testid="reminder-enabled-switch" />
        </div>
        <div className="grid gap-1.5 max-w-md">
          <Label className="text-xs font-medium">
            {t("reminderSettings.replyToLabel")}
          </Label>
          <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)}
                 placeholder="factures@monentreprise.fr"
                 disabled={!canEdit}
                 data-testid="reminder-replyto-input" />
          <div className="text-xs text-slate-500">{t("reminderSettings.replyToHelp")}</div>
        </div>
      </Card>

      {/* Ladder */}
      <Card className="p-6 space-y-4" data-testid="reminder-ladder-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-slate-900">{t("reminderSettings.ladderTitle")}</div>
            <div className="text-xs text-slate-500 mt-0.5 max-w-md">
              {t("reminderSettings.ladderHelp")}
            </div>
          </div>
          {canEdit && defaults && (
            <Button variant="outline" size="sm" onClick={resetDefaults}
                    data-testid="reminder-reset-defaults">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
              {t("reminderSettings.resetDefaults")}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {ladder.length === 0 && (
            <div className="text-sm text-slate-500 py-3 px-3 bg-slate-50 rounded-md">
              {t("reminderSettings.ladderEmpty")}
            </div>
          )}
          {ladder.map((s, i) => {
            const isAuto = LEVELS.find(l => l.value === s.level)?.auto;
            return (
              <div key={i}
                   className="rounded-md border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center"
                   data-testid={`ladder-step-${i}`}>
                <div className="sm:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("reminderSettings.stepDays")}
                  </Label>
                  <Input type="number" min="0" max="365"
                         value={s.days_after_due}
                         onChange={(e) => updateStep(i, { days_after_due: e.target.value })}
                         disabled={!canEdit}
                         data-testid={`ladder-step-days-${i}`}
                         className="mt-1" />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("reminderSettings.stepLevel")}
                  </Label>
                  <Select value={s.level}
                          onValueChange={(v) => updateStep(i, { level: v })}
                          disabled={!canEdit}>
                    <SelectTrigger className="mt-1 h-9"
                                   data-testid={`ladder-step-level-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map(l => (
                        <SelectItem key={l.value} value={l.value}>
                          {t(`reminderSettings.levels.${l.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("reminderSettings.stepTemplate")}
                  </Label>
                  <Select value={s.template}
                          onValueChange={(v) => updateStep(i, { template: v })}
                          disabled={!canEdit}>
                    <SelectTrigger className="mt-1 h-9"
                                   data-testid={`ladder-step-template-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(TEMPLATE_BY_LEVEL[s.level] || []).map(tpl => (
                        <SelectItem key={tpl} value={tpl}>
                          {t(`reminderSettings.templates.${tpl}`, tpl)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2 flex items-end justify-between gap-2 h-full">
                  <Badge variant="outline"
                         className={cn("text-[10px]",
                           isAuto
                             ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                             : "border-amber-200 bg-amber-50 text-amber-800")}>
                    {isAuto
                      ? t("reminderSettings.autoSend")
                      : t("reminderSettings.needsApproval")}
                  </Badge>
                  {canEdit && (
                    <Button variant="ghost" size="sm"
                            onClick={() => removeStep(i)}
                            data-testid={`ladder-step-remove-${i}`}
                            className="text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {canEdit && (
          <Button variant="outline" onClick={addStep} className="w-full"
                  data-testid="ladder-add-step">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
            {t("reminderSettings.addStep")}
          </Button>
        )}

        {/* Preview timeline */}
        {sortedSteps.length > 0 && (
          <div className="pt-4 border-t border-slate-100">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-3">
              {t("reminderSettings.previewTitle")}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800">
                {t("reminderSettings.previewDue")}
              </span>
              {sortedSteps.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <span className="text-slate-400">→</span>
                  <span className={cn(
                    "px-2 py-1 rounded border",
                    s.level === "reminder"
                      ? "bg-slate-50 border-slate-200 text-slate-700"
                      : "bg-amber-50 border-amber-200 text-amber-800")}>
                    +{s.days_after_due}j · {t(`reminderSettings.levelsShort.${s.level}`)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Partner handoff */}
      <Card className="p-6 space-y-4" data-testid="reminder-partner-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-slate-900">
              {t("reminderSettings.partnerTitle")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 max-w-md">
              {t("reminderSettings.partnerHelp")}
            </div>
          </div>
          <Switch checked={!!partner.enabled}
                  onCheckedChange={(v) => setPartner({ ...partner, enabled: v })}
                  disabled={!canEdit}
                  data-testid="partner-enabled-switch" />
        </div>
        {partner.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t("reminderSettings.partnerName")}</Label>
              <Input value={partner.partner_name || ""}
                     onChange={(e) => setPartner({ ...partner, partner_name: e.target.value })}
                     disabled={!canEdit}
                     data-testid="partner-name-input"
                     className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">{t("reminderSettings.partnerEmail")}</Label>
              <Input type="email" value={partner.partner_email || ""}
                     onChange={(e) => setPartner({ ...partner, partner_email: e.target.value })}
                     disabled={!canEdit}
                     data-testid="partner-email-input"
                     className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">{t("reminderSettings.partnerWebhook")}</Label>
              <Input value={partner.webhook_url || ""}
                     onChange={(e) => setPartner({ ...partner, webhook_url: e.target.value })}
                     disabled={!canEdit}
                     placeholder="https://…"
                     data-testid="partner-webhook-input"
                     className="mt-1" />
              <div className="text-[11px] text-slate-500 mt-1">
                {t("reminderSettings.partnerWebhookHelp")}
              </div>
            </div>
            <div className="sm:col-span-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
              <span>{t("reminderSettings.partnerWarning")}</span>
            </div>
          </div>
        )}
      </Card>

      {canEdit && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button onClick={save} disabled={saving}
                  data-testid="reminder-save-btn">
            {saving
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Save className="h-4 w-4 mr-1.5" strokeWidth={2} />}
            {t("common.save")}
          </Button>
        </div>
      )}
    </div>
  );
}
